// TodoAssignee.tsx — who has a todo, and the acts to take it on or hand it over.
//
// Shown to every signed-in viewer, not only admins: anyone with write access
// to the todo's code may pick up blocked work or delegate it to another such
// person. The server decides; a refusal is shown under the buttons. The
// picker lists the family's people only, never bots or automation logins.

import { useState, type FormEvent } from 'react';

import type { ShiftTodo } from '../../api/types';
import { ActionButton } from '../../components/action/ActionButton';
import { useShiftTodoAction } from '../../hooks/useShift';
import { usePeople } from '../../hooks/usePeople';
import { FINISHED, assigneeChoices, assigneeLine, type Viewer } from './assigneeModel';

export function TodoAssignee({
  todo,
  viewer,
}: {
  todo: ShiftTodo;
  viewer: Viewer | null;
}): JSX.Element | null {
  const action = useShiftTodoAction();
  const choices = assigneeChoices(todo, viewer);
  const [picking, setPicking] = useState(false);
  // `done` cannot be undone: it asks first, as the admin menu's Mark done does.
  const [finishing, setFinishing] = useState(false);
  const people = usePeople(todo.family, todo.id, picking);
  const [target, setTarget] = useState('');
  const run = (request: Parameters<typeof action.mutate>[0], after?: () => void): void =>
    action.mutate(request, { onSuccess: after });
  const base = { family: todo.family, id: todo.id };
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!target) return;
    run({ ...base, action: 'assign', value: target }, () => {
      setPicking(false);
      setTarget('');
    });
  };
  // Not the holder, and not the viewer on a free todo: taking it yourself is
  // "I'll take it", not a hand-over.
  const candidates = (people.data ?? []).filter(
    (person) =>
      person.login !== todo.assignee && (todo.assignee || person.login !== viewer?.login)
  );
  // Finished work nobody took on has nothing to say here.
  if (FINISHED.has(todo.status) && !todo.assignee) return null;
  return (
    <div className="shift__assignee" data-testid="todo-assignee">
      <span className="shift__assignee-line" data-testid="todo-assignee-line">
        {assigneeLine(todo, viewer?.login ?? null)}
      </span>
      <span className="shift__actions">
        {choices.claim ? (
          <ActionButton
            variant="primary"
            disabled={action.isPending}
            onClick={() => run({ ...base, action: 'claim' })}
            aria-label={`Claim ${todo.id}`}
          >
            I'll take it
          </ActionButton>
        ) : null}
        {choices.done ? (
          <ActionButton
            variant="primary"
            disabled={action.isPending}
            aria-expanded={finishing}
            onClick={() => setFinishing((open) => !open)}
            aria-label={`Mark ${todo.id} done`}
          >
            Mark done
          </ActionButton>
        ) : null}
        {choices.assign ? (
          <ActionButton
            variant="default"
            disabled={action.isPending}
            aria-expanded={picking}
            onClick={() => setPicking((open) => !open)}
            aria-label={`Assign ${todo.id} to someone`}
          >
            Assign to…
          </ActionButton>
        ) : null}
        {choices.unassign ? (
          <ActionButton
            variant="default"
            disabled={action.isPending}
            onClick={() => run({ ...base, action: 'unassign' })}
            aria-label={`Unassign ${todo.id}`}
          >
            {todo.assignee === viewer?.login ? 'Give it back' : 'Unassign'}
          </ActionButton>
        ) : null}
      </span>
      {finishing ? (
        <form
          className="shift__block-form"
          aria-label={`Mark ${todo.id} done?`}
          onSubmit={(event) => {
            event.preventDefault();
            run({ ...base, action: 'done' }, () => setFinishing(false));
          }}
        >
          <span>Mark {todo.id} done? This cannot be undone.</span>
          <ActionButton type="submit" variant="primary" disabled={action.isPending}>
            Mark done
          </ActionButton>
          <ActionButton variant="default" onClick={() => setFinishing(false)}>
            Cancel
          </ActionButton>
        </form>
      ) : null}
      {picking ? (
        <form className="shift__block-form" onSubmit={submit} aria-label={`Assign ${todo.id}`}>
          {people.isPending ? (
            <span>Loading people…</span>
          ) : people.error ? (
            <span role="alert">Could not load people: {people.error.message}</span>
          ) : (
            <select
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              aria-label={`Person to assign ${todo.id} to`}
            >
              <option value="">Pick a person</option>
              {candidates.map((person) => (
                <option key={person.login} value={person.login}>
                  {person.display_name && person.display_name !== person.login
                    ? `${person.display_name} (${person.login})`
                    : person.login}
                </option>
              ))}
            </select>
          )}
          <ActionButton type="submit" variant="primary" disabled={!target || action.isPending}>
            Assign
          </ActionButton>
          <ActionButton variant="default" onClick={() => setPicking(false)}>
            Cancel
          </ActionButton>
        </form>
      ) : null}
      {action.error ? (
        <span className="shift__error" role="alert">
          {action.error.message}
        </span>
      ) : null}
    </div>
  );
}
